// features/angleCalculations.tsx
import { Point } from "./types";

/**
 * Calculate the inner angle at a vertex in a polygon
 * @param prev Previous point in the polygon
 * @param current Current point (vertex where we calculate the angle)
 * @param next Next point in the polygon
 * @returns The inner angle in degrees (0-360)
 */
export function calculateInnerAngle(
  prev: Point,
  current: Point,
  next: Point
): number {
  // Vector from current to previous
  const v1x = prev.x - current.x;
  const v1z = prev.z - current.z;

  // Vector from current to next
  const v2x = next.x - current.x;
  const v2z = next.z - current.z;

  // Calculate the angle using atan2
  const angle1 = Math.atan2(v1z, v1x);
  const angle2 = Math.atan2(v2z, v2x);

  // Calculate the difference
  let angle = angle2 - angle1;

  // Normalize to 0-2π range
  while (angle < 0) angle += 2 * Math.PI;
  while (angle > 2 * Math.PI) angle -= 2 * Math.PI;

  // Convert to degrees
  let degrees = (angle * 180) / Math.PI;

  // For a polygon traversed counterclockwise, we want the interior angle
  // If the angle is greater than 180, take the reflex angle
  if (degrees > 180) {
    degrees = 360 - degrees;
  }

  return Math.round(degrees * 10) / 10; // Round to 1 decimal place
}

/**
 * Calculate all inner angles for a room polygon
 * @param polygon Array of points forming the room polygon
 * @returns Array of angles, one for each vertex
 */
export function calculateRoomAngles(polygon: Point[]): number[] {
  if (polygon.length < 3) return [];

  const angles: number[] = [];

  for (let i = 0; i < polygon.length; i++) {
    const prev = polygon[(i - 1 + polygon.length) % polygon.length];
    const current = polygon[i];
    const next = polygon[(i + 1) % polygon.length];

    angles.push(calculateInnerAngle(prev, current, next));
  }

  return angles;
}

/**
 * Calculate the position for placing an angle label near a vertex
 * @param prev Previous point in the polygon
 * @param current Current point (vertex)
 * @param next Next point in the polygon
 * @param offset Distance from the vertex to place the label
 * @returns Point where the label should be positioned
 */
export function calculateAngleLabelPosition(
  prev: Point,
  current: Point,
  next: Point,
  offsetX: number = 20,
  offsetZ: number = 10
): Point {
  // Calculate the bisector of the angle
  // Normalize the vectors from current to prev and next
  const v1x = prev.x - current.x;
  const v1z = prev.z - current.z;
  const len1 = Math.sqrt(v1x * v1x + v1z * v1z);

  const v2x = next.x - current.x;
  const v2z = next.z - current.z;
  const len2 = Math.sqrt(v2x * v2x + v2z * v2z);

  // Normalized vectors
  const n1x = v1x / len1;
  const n1z = v1z / len1;
  const n2x = v2x / len2;
  const n2z = v2z / len2;

  // Bisector direction (average of normalized vectors)
  const bisectorX = (n1x + n2x) / 2;
  const bisectorZ = (n1z + n2z) / 2;
  const bisectorLen = Math.sqrt(
    bisectorX * bisectorX + bisectorZ * bisectorZ
  );

  // Normalize the bisector
  const normBisectorX = bisectorX / bisectorLen;
  const normBisectorZ = bisectorZ / bisectorLen;

  // Place label along the bisector at the specified offset
  return {
    x: current.x + normBisectorX * offsetX,
    z: current.z + normBisectorZ * offsetZ,
  };
}